import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { useDistributors } from '@/hooks/useDistributors';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { saveConsignment } from '@/services/consignments';

interface ConsignmentModalProps {
  onClose: () => void;
  onSaved: () => void;
}

interface LineItem {
  variantId: string;
  quantity: string;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Activa' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'settled', label: 'Liquidada' },
  { value: 'closed', label: 'Cerrada' },
  { value: 'returned', label: 'Devuelta' },
];

export default function ConsignmentModal({ onClose, onSaved }: ConsignmentModalProps) {
  const { data: distributors, loading: loadingDistributors } = useDistributors();
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [distributorId, setDistributorId] = useState('');
  const [status, setStatus] = useState('active');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItem[]>([{ variantId: '', quantity: '1' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVariants().then((r) => setVariants(r.data));
  }, []);

  const totalPares = useMemo(
    () => lines.reduce((sum, l) => (l.variantId ? sum + (Number(l.quantity) || 0) : sum), 0),
    [lines],
  );

  function updateLine(i: number, patch: Partial<LineItem>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [...ls, { variantId: '', quantity: '1' }]);
  }
  function removeLine(i: number) {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!distributorId) {
      setError('Selecciona un distribuidor para continuar.');
      return;
    }

    const items = lines
      .filter((l) => l.variantId)
      .map((l) => {
        const v = variants.find((x) => x.id === l.variantId)!;
        return {
          variantId: l.variantId,
          quantity: Math.max(1, Number(l.quantity) || 1),
          unitPrice: v.retailPrice,
          unitCost: v.unitCost,
        };
      });

    setSaving(true);
    const { error: saveError } = await saveConsignment({ distributorId, status, notes, items });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title="Nueva Consignación"
      subtitle="Registra la entrega de mercancía a un distribuidor."
      onClose={onClose}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Distribuidor *</label>
            <select
              className={inputCls}
              value={distributorId}
              onChange={(e) => setDistributorId(e.target.value)}
            >
              <option value="">
                {loadingDistributors ? 'Cargando distribuidores...' : 'Selecciona un distribuidor'}
              </option>
              {distributors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.businessName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Estado</label>
            <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Productos entregados</label>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="border border-background-200 rounded-md p-2 space-y-1.5">
                <select
                  className={`${inputCls} w-full`}
                  value={l.variantId}
                  onChange={(e) => updateLine(i, { variantId: e.target.value })}
                >
                  <option value="">Selecciona producto y talla...</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.productName} · {v.sizeName} ({v.available} disp.)
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">Cantidad</span>
                  <input
                    type="number"
                    min="1"
                    className={`${inputCls} w-24`}
                    value={l.quantity}
                    onChange={(e) => updateLine(i, { quantity: e.target.value })}
                  />
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="ml-auto w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Quitar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addLine}
            className="mt-2 text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1"></i>Agregar producto
          </button>
        </div>

        <div className="flex items-center justify-between bg-accent-50 border border-accent-100 rounded-md px-4 py-3">
          <span className="text-sm text-foreground-700">Pares entregados</span>
          <span className="text-lg font-bold text-foreground-950">{totalPares}</span>
        </div>

        <div>
          <label className={labelCls}>Notas</label>
          <textarea
            className={inputCls}
            rows={3}
            value={notes}
            maxLength={500}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Referencias entregadas, condiciones comerciales, etc."
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || loadingDistributors}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-1.5"></i>Crear consignación
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}